import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Typography,
    Box,
    TablePagination,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { getAllMonitors, deleteMonitor } from '../../api';

const MonitorList = () => {
    const [monitors, setMonitors] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalMonitors, setTotalMonitors] = useState(0);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [monitorToDelete, setMonitorToDelete] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchMonitors(currentPage + 1, rowsPerPage);
    }, [currentPage, rowsPerPage]);

    const fetchMonitors = async (page, limit) => {
        try {
            const response = await getAllMonitors({ page, limit });
            setMonitors(response.data.monitors);
            setTotalMonitors(response.data.totalMonitors);
        } catch (error) {
            console.error('Error fetching monitors:', error);
        }
    };

    const handleCreateMonitor = () => {
        navigate('/create-monitor');
    };

    const handleEditMonitor = (id) => {
        navigate(`/edit-monitor/${id}`);
    };

    const handleDeleteMonitor = async () => {
        try {
            await deleteMonitor(monitorToDelete);
            fetchMonitors(currentPage + 1, rowsPerPage);
            setDeleteDialogOpen(false);
        } catch (error) {
            console.error('Error deleting monitor:', error);
        }
    };

    const handleChangePage = (event, newPage) => {
        setCurrentPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };

    return (
        <Box>
            {monitors.length === 0 ? (
                <Box textAlign="center" mt={4}>
                    <Typography variant="h6">No monitors available.</Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCreateMonitor}
                        startIcon={<AddCircleIcon />}
                        style={{ marginTop: 10 }}
                    >
                        Create New Monitor
                    </Button>
                </Box>
            ) : (
                <>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCreateMonitor}
                        startIcon={<AddCircleIcon />}
                        style={{ marginBottom: 10 }}
                    >
                        Create New Monitor
                    </Button>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Monitor Name</TableCell>
                                    <TableCell>Entity Type</TableCell>
                                    <TableCell>Applies to All Entities</TableCell>
                                    <TableCell>Specific Entities</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {monitors.map((monitor) => (
                                    <TableRow key={monitor._id}>
                                        <TableCell>{monitor.name}</TableCell>
                                        <TableCell>{monitor.entityTypeId?.name || 'N/A'}</TableCell>
                                        <TableCell>{monitor.appliesToAllEntities ? 'Yes' : 'No'}</TableCell>
                                        <TableCell>
                                            {monitor.specificEntities?.length > 0
                                                ? monitor.specificEntities.join(', ')
                                                : 'N/A'}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                color="primary"
                                                onClick={() => handleEditMonitor(monitor._id)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                color="secondary"
                                                onClick={() => {
                                                    setMonitorToDelete(monitor._id);
                                                    setDeleteDialogOpen(true);
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={totalMonitors}
                        page={currentPage}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />

                    <Dialog
                        open={deleteDialogOpen}
                        onClose={() => setDeleteDialogOpen(false)}
                    >
                        <DialogTitle>Delete Monitor</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                Are you sure you want to delete this monitor? This action cannot be undone.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                            <Button
                                onClick={handleDeleteMonitor}
                                color="secondary"
                                variant="contained"
                            >
                                Delete
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}
        </Box>
    );
};

export default MonitorList;
